import React, { memo } from 'react';
import { RichText, RichTextBlock } from 'prismic-reactjs';
import Section from '@components/section';
import Wrapper from '@components/wrapper.styled';
import QuoteIcon from '@components/svg-icons/quote';
import { defaultTheme } from '@styles/theme.styled';
import {
  QuoteContainer,
  MainQuoteContainer,
  QuoteIconWrapper,
  QuoteContent,
  Author,
  AuthorName,
  AuthorTitle,
} from './quote.styled';

interface QuoteProps {
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  quote?: RichTextBlock[] | null;
  authorName?: string | null;
  authorTitle?: string | null;
}

const getTextColor = (
  backgroundColor: keyof typeof defaultTheme.backgroundColors
): keyof typeof defaultTheme.colors => {
  if (['dark', 'primary', 'gradient'].includes(backgroundColor)) {
    return 'white';
  }

  return 'dark';
};

const Quote = memo(
  ({
    backgroundColor = 'primary',
    quote = [],
    authorName,
    authorTitle,
  }: QuoteProps): JSX.Element | null => {
    const normalizedQuote = Array.isArray(quote) ? quote : [];
    const hasQuote = normalizedQuote.some((block) => block.text?.trim().length);
    const hasAuthor = Boolean(authorName || authorTitle);

    if (!hasQuote && !hasAuthor) {
      return null;
    }

    const fontColor = getTextColor(backgroundColor);

    return (
      <Section backgroundColor={backgroundColor}>
        <Wrapper widthSize="md" alignment="center">
          <QuoteContainer fontColor={fontColor}>
            <MainQuoteContainer>
              <QuoteIconWrapper fontColor="primary" aria-hidden>
                <QuoteIcon />
              </QuoteIconWrapper>
              {hasQuote && (
                <QuoteContent fontColor={fontColor}>
                  <RichText render={normalizedQuote} />
                </QuoteContent>
              )}
            </MainQuoteContainer>
            {hasAuthor && (
              <Author fontColor={fontColor}>
                {authorName ? <AuthorName>{authorName}</AuthorName> : null}
                {authorTitle ? (
                  <AuthorTitle fontColor={fontColor}>{authorTitle}</AuthorTitle>
                ) : null}
              </Author>
            )}
          </QuoteContainer>
        </Wrapper>
      </Section>
    );
  }
);

Quote.displayName = 'Quote';

export default Quote;
